import { TransportProvider } from '@connectrpc/connect-query';
import * as Sentry from '@sentry/react';
import {
	MutationCache,
	QueryCache,
	QueryClient,
	QueryClientProvider,
} from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { APIProvider } from '@vis.gl/react-google-maps';
import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';

import { ToastRegion } from './components/ToastRegion/ToastRegion.tsx';
import { handleApiError } from './config/apiErrorHandler.ts';
import { config } from './config/config.ts';
import { getTransport } from './config/transport.ts';
import { I18nReady } from './I18nReady.tsx';
import { InvalidateQueriesOnLanguageChange } from './InvalidateQueriesOnLanguageChange.tsx';
import { ThemeProvider } from './lib/hooks/useTheme.ts';
import { initSentry } from './lib/sentry.ts';

import './main.css';
import { routeTree } from './routeTree.gen';

const transport = getTransport();
const queryClient = new QueryClient({
	mutationCache: new MutationCache({
		onError(error, _variables, _context, mutation) {
			handleApiError(error, mutation.options.meta?.errorMessage);
		},
	}),
	queryCache: new QueryCache({
		onError(error, query) {
			handleApiError(error, query.options.meta?.errorMessage);
		},
	}),
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
			refetchOnMount: false,
			refetchIntervalInBackground: false,
			refetchInterval: 0,
			retry: 1,
		},
	},
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router;
	}
}

const router = createRouter({ routeTree });

initSentry(router);

const container = document.getElementById('root');

if (!container) {
	throw new Error('Root element not found');
}

// @TODO: Replace with a proper loading component
// oxlint-disable-next-line react/only-export-components -- entry point, fast refresh doesn't apply
function Loading() {
	return (
		<div
			style={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				minHeight: '100vh',
			}}
		>
			Loading…
		</div>
	);
}

const root = createRoot(container, {
	// Callback called when an error is thrown and not caught by an ErrorBoundary.
	onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
		console.error('Uncaught error', error, errorInfo.componentStack);
	}),
	// Callback called when React catches an error in an ErrorBoundary.
	onCaughtError: Sentry.reactErrorHandler(),
	// Callback called when React automatically recovers from errors.
	onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
	<StrictMode>
		<ThemeProvider>
			<TransportProvider transport={transport}>
				<QueryClientProvider client={queryClient}>
					<InvalidateQueriesOnLanguageChange />
					<ToastRegion />
					<APIProvider
						apiKey={config.googleMapsApiKey ?? ''}
						libraries={['geometry', 'marker']}
					>
						<Suspense fallback={<Loading />}>
							<I18nReady>
								<RouterProvider router={router} />
							</I18nReady>
						</Suspense>
					</APIProvider>
				</QueryClientProvider>
			</TransportProvider>
		</ThemeProvider>
	</StrictMode>,
);
